import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SynchronizationService from '../services/synchronizationService';
import { Toast } from '../components/ui';
import { useOmieBudgetImport } from '../contexts/OmieBudgetImportContext';

const monthNames = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

function buildIdToNodeMap(tree) {
  const map = new Map();
  const visit = (node) => {
    map.set(node.id, node);
    if (Array.isArray(node.children)) {
      node.children.forEach(visit);
    }
  };
  tree.forEach(visit);
  return map;
}

function computeLeafIdsByNode(tree) {
  const leafIdsByNode = new Map();
  const dfs = (node) => {
    if (!node.children || node.children.length === 0) {
      leafIdsByNode.set(node.id, [node.id]);
      return [node.id];
    }
    const all = [];
    node.children.forEach((child) => {
      const childLeaves = dfs(child);
      all.push(...childLeaves);
    });
    leafIdsByNode.set(node.id, all);
    return all;
  };
  tree.forEach(dfs);
  return leafIdsByNode;
}

/**
 * Arredonda um número para 2 casas decimais de forma estável.
 * Usa Number.EPSILON para evitar problemas de arredondamento (ex: 1.005 → 1.01).
 */
function round2(value) {
  if (typeof value !== 'number' || Number.isNaN(value)) return value;
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

/**
 * Aplica o percentual por natureza em cima de um valor base.
 * Regra ÚNICA (para VM, VO e VP):
 * - Se percentual for 0%, o valor permanece exatamente o mesmo (valor cru).
 * - Receitas (R): novo = base * (1 + percReceitas/100)
 * - Despesas (P): novo = base * (1 - percDespesas/100)
 *
 * Se o valor base não for número, o valor é mantido.
 * Cada natureza usa apenas o seu percentual correspondente.
 */
function applyPercentByNature({ base, natureza, percReceitas, percDespesas }) {
  if (typeof base !== 'number' || Number.isNaN(base)) return base;

  const nat = (natureza || 'R').toUpperCase();

  const rawR = Number(percReceitas);
  const rawP = Number(percDespesas);

  const hasR = !Number.isNaN(rawR) && rawR !== 0;
  const hasP = !Number.isNaN(rawP) && rawP !== 0;

  // 0% → valor cru, sem alteração
  if (!hasR && !hasP) {
    return base;
  }

  if (nat === 'R' && hasR) {
    // Receita → aumenta com o percentual
    const factor = 1 + rawR / 100;
    return round2(base * factor);
  }

  if (nat === 'P' && hasP) {
    // Despesa → diminui com o percentual (valor fica menos negativo / gasto menor)
    const factor = 1 - rawP / 100;
    return round2(base * factor);
  }

  // Se não se aplica nenhuma condição (ex: natureza R e só percDespesas setado),
  // devolve o valor original (ignora).
  return base;
}

export default function useBudgetConfig(syncId, syncName) {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [loading, setLoading] = useState(false);
  // Ano efetivamente carregado no grid (o resultado da Omie só é aplicado sobre o ano correspondente)
  const [loadedYear, setLoadedYear] = useState(null);
  const { imports: omieImports, startImport, resumeImport, dismissImport, registerScreen } = useOmieBudgetImport();
  const omieJob = syncId ? omieImports[syncId] : undefined;
  const importOmieLoading = omieJob?.status === 'running';
  const [categories, setCategories] = useState([]);
  const [showVM, setShowVM] = useState(true);
  const [showVP, setShowVP] = useState(true);
  const [showVO, setShowVO] = useState(true);
  // budgets: { [categoryId]: { [month]: { vm, vp, vo } } }
  const [budgets, setBudgets] = useState({});
  // track manual edits for VM (to protect on "Completar")
  const [touchedVM, setTouchedVM] = useState(new Set());

  const idToNode = useMemo(() => buildIdToNodeMap(categories), [categories]);
  const leafIdsByNode = useMemo(() => computeLeafIdsByNode(categories), [categories]);

  const isLeaf = useCallback((categoryId) => {
    const node = idToNode.get(categoryId);
    return node ? !node.children || node.children.length === 0 : true;
  }, [idToNode]);

  const loadCategories = useCallback(async () => {
    if (!syncId) return;
    try {
      setLoading(true);
      const tree = await SynchronizationService.getBudgetCategories(syncId);
      // normaliza para garantir 'children' array e preservar natureza
      const normalize = (nodes) => (nodes || []).map((n) => ({
        id: n.id,
        name: n.name,
        parentId: n.parentId ?? null,
        natureza: n.natureza ?? n.Natureza ?? null,
        children: normalize(n.children || []),
      }));
      setCategories(normalize(Array.isArray(tree) ? tree : []));
    } catch (e) {
      console.error(e);
      Toast.error('Erro ao carregar categorias de orçamento');
    } finally {
      setLoading(false);
    }
  }, [syncId]);

  const loadBudget = useCallback(async (targetYear) => {
    if (!syncId || !targetYear) return;
    try {
      setLoading(true);
      const result = await SynchronizationService.getBudget(syncId, targetYear);
      const items = Array.isArray(result?.itens) ? result.itens : Array.isArray(result) ? result : [];
      const next = {};
      items.forEach((it) => {
        const cat = it.categoriaId || it.CategoriaId;
        const mes = Number(it.mes || it.Mes);
        if (!cat || !mes) return;
        if (!next[cat]) next[cat] = {};
        next[cat][mes] = {
          vm: it.valorMedio ?? it.ValorMedio ?? undefined,
          vp: it.valorPessimista ?? it.ValorPessimista ?? undefined,
          vo: it.valorOtimista ?? it.ValorOtimista ?? undefined,
        };
      });
      setBudgets(next);
      setTouchedVM(new Set());
      setLoadedYear(targetYear);
    } catch (e) {
      console.error(e);
      Toast.error('Erro ao carregar orçamento');
    } finally {
      setLoading(false);
    }
  }, [syncId]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  useEffect(() => {
    loadBudget(year);
  }, [year, loadBudget]);

  const setValue = useCallback((categoryId, month, field, value) => {
    setBudgets(prev => {
      const next = { ...prev };
      if (!next[categoryId]) next[categoryId] = {};
      const monthObj = { ...(next[categoryId][month] || {}) };
      monthObj[field] = value;
      next[categoryId][month] = monthObj;
      return next;
    });
    if (field === 'vm') {
      setTouchedVM(prev => {
        const next = new Set(prev);
        next.add(`${categoryId}|${month}`);
        return next;
      });
    }
  }, []);

  const getAggregatedValue = useCallback((categoryId, month, field) => {
    const leaves = leafIdsByNode.get(categoryId) || [categoryId];
    let sum = 0;
    let hasAny = false;
    leaves.forEach((leafId) => {
      const monthObj = budgets[leafId]?.[month];
      const val = monthObj ? monthObj[field] : undefined;
      if (typeof val === 'number') {
        sum += val;
        hasAny = true;
      }
    });
    return hasAny ? sum : undefined;
  }, [budgets, leafIdsByNode]);

  /**
   * Recalcula a VISÃO MÉDIA (VM) por natureza usando a regra única:
   * - Receitas (R): VM = VM * (1 + percReceitas/100)
   * - Despesas (P): VM = VM * (1 - percDespesas/100)
   * - Se percentual for 0%, mantém o valor cru
   */
  const recalculateVMByNature = useCallback(({ percReceitas, percDespesas }) => {
    setBudgets(prev => {
      const next = {};
      Object.keys(prev).forEach((catId) => {
        const node = idToNode.get(catId);
        const natureza = node?.natureza ?? node?.Natureza ?? 'R';
        next[catId] = {};
        for (let m = 1; m <= 12; m++) {
          const cur = prev[catId][m] || {};
          const baseVm = typeof cur.vm === 'number' ? cur.vm : cur.vm;
          const newVm = applyPercentByNature({
            base: baseVm,
            natureza,
            percReceitas,
            percDespesas,
          });
          next[catId][m] = {
            vm: newVm,
            vp: cur.vp,
            vo: cur.vo,
          };
        }
      });
      return next;
    });
  }, [idToNode]);

  /**
   * Recalcula VISÃO OTIMISTA (VO) por natureza a partir da VM,
   * usando a mesma regra:
   * - Receitas (R): VO = VM * (1 + percReceitas/100)
   * - Despesas (P): VO = VM * (1 - percDespesas/100)
   * - Sempre usa VM como base (não altera VM)
   */
  const recalculateVOByNature = useCallback(({ percReceitas, percDespesas }) => {
    setBudgets(prev => {
      const next = {};
      Object.keys(prev).forEach((catId) => {
        const node = idToNode.get(catId);
        const natureza = node?.natureza ?? node?.Natureza ?? 'R';
        next[catId] = {};
        for (let m = 1; m <= 12; m++) {
          const cur = prev[catId][m] || {};
          const baseVm = typeof cur.vm === 'number' ? cur.vm : cur.vm;
          const newVo = applyPercentByNature({
            base: baseVm,
            natureza,
            percReceitas,
            percDespesas,
          });
          next[catId][m] = {
            vm: cur.vm,
            vp: cur.vp,
            vo: newVo,
          };
        }
      });
      return next;
    });
  }, [idToNode]);

  /**
   * Recalcula VISÃO PESSIMISTA (VP) por natureza a partir da VM,
   * usando a MESMA regra (o que muda é só o percentual que o usuário informa):
   * - Receitas (R): VP = VM * (1 + percReceitas/100)
   * - Despesas (P): VP = VM * (1 - percDespesas/100)
   * - Sempre usa VM como base (não altera VM nem VO)
   */
  const recalculateVPByNature = useCallback(({ percReceitas, percDespesas }) => {
    setBudgets(prev => {
      const next = {};
      Object.keys(prev).forEach((catId) => {
        const node = idToNode.get(catId);
        const natureza = node?.natureza ?? node?.Natureza ?? 'R';
        next[catId] = {};
        for (let m = 1; m <= 12; m++) {
          const cur = prev[catId][m] || {};
          const baseVm = typeof cur.vm === 'number' ? cur.vm : cur.vm;
          const newVp = applyPercentByNature({
            base: baseVm,
            natureza,
            percReceitas,
            percDespesas,
          });
          next[catId][m] = {
            vm: cur.vm,
            vp: newVp,
            vo: cur.vo,
          };
        }
      });
      return next;
    });
  }, [idToNode]);

  const fillFromAPI = useCallback(async (mode) => {
    if (!syncId || !year) return;
    try {
      setLoading(true);
      const data = await SynchronizationService.fillBudgetWithRealized(syncId, year);
      const mapMeses = Array.isArray(data?.mapMeses) ? data.mapMeses : [];
      const realizadoMensal = Array.isArray(data?.realizadoMensal) ? data.realizadoMensal : [];

      const refByBudgetMonth = {};
      mapMeses.forEach(m => {
        refByBudgetMonth[Number(m.mesOrcamento)] = {
          anoRef: Number(m.anoReferencia),
          mesRef: Number(m.mesReferencia)
        };
      });

      const realizedIndex = new Map();
      realizadoMensal.forEach(r => {
        const key = `${r.categoriaId}|${Number(r.anoRef)}|${Number(r.mesRef)}`;
        realizedIndex.set(key, Number(r.valorRealizado));
      });

      setBudgets(prev => {
        const next = { ...prev };

        // ensure all leaf categories exist in map
        const ensureLeaf = (catId) => {
          if (!next[catId]) next[catId] = {};
        };

        const uniqueLeaves = new Set();
        const collectLeaves = (nodes) => {
          nodes.forEach(n => {
            if (n.children && n.children.length > 0) {
              collectLeaves(n.children);
            } else {
              uniqueLeaves.add(n.id);
            }
          });
        };
        collectLeaves(categories);

        uniqueLeaves.forEach((leafId) => {
          ensureLeaf(leafId);
          for (let m = 1; m <= 12; m++) {
            const ref = refByBudgetMonth[m];
            const key = ref ? `${leafId}|${ref.anoRef}|${ref.mesRef}` : null;
            const realized = key ? realizedIndex.get(key) : undefined;
            const cur = next[leafId][m] || {};
            if (mode === 'overwrite') {
              next[leafId][m] = {
                ...cur,
                vm: typeof realized === 'number' ? Number(realized.toFixed(2)) : 0
              };
            } else {
              // complete only if not touched
              const touched = touchedVM.has(`${leafId}|${m}`);
              if (!touched) {
                next[leafId][m] = {
                  ...cur,
                  vm: typeof realized === 'number' ? Number(realized.toFixed(2)) : (cur.vm ?? 0)
                };
              }
            }
          }
        });

        return next;
      });

      if (mode === 'overwrite') {
        setTouchedVM(new Set());
      }
      Toast.success('Valores médios carregados da API');
    } catch (e) {
      console.error(e);
      Toast.error('Erro ao preencher valores médios da API');
    } finally {
      setLoading(false);
    }
  }, [categories, leafIdsByNode, syncId, year, touchedVM]);

  /**
   * Somente Omie: aplica no VM o orçamento (valor previsto) importado da Omie.
   * Não salva — o usuário revisa e clica em "Salvar Orçamento".
   */
  const applyOmieResult = useCallback((resultado, mode) => {
    const itens = Array.isArray(resultado?.itens) ? resultado.itens : [];

    const imported = new Map();
    itens.forEach((it) => {
      const cat = it.categoriaId || it.CategoriaId;
      const mes = Number(it.mes || it.Mes);
      if (!cat || !mes) return;
      imported.set(`${cat}|${mes}`, Number(it.valorMedio ?? it.ValorMedio ?? 0));
    });

    setBudgets(prev => {
      const next = { ...prev };
      const uniqueLeaves = new Set();
      const collectLeaves = (nodes) => {
        nodes.forEach(n => {
          if (n.children && n.children.length > 0) {
            collectLeaves(n.children);
          } else {
            uniqueLeaves.add(n.id);
          }
        });
      };
      collectLeaves(categories);

      uniqueLeaves.forEach((leafId) => {
        next[leafId] = { ...(next[leafId] || {}) };
        for (let m = 1; m <= 12; m++) {
          const value = imported.get(`${leafId}|${m}`);
          const cur = next[leafId][m] || {};
          if (mode === 'overwrite') {
            next[leafId][m] = { ...cur, vm: typeof value === 'number' ? round2(value) : 0 };
          } else if (!touchedVM.has(`${leafId}|${m}`)) {
            next[leafId][m] = { ...cur, vm: typeof value === 'number' ? round2(value) : (cur.vm ?? 0) };
          }
        }
      });
      return next;
    });

    if (mode === 'overwrite') {
      setTouchedVM(new Set());
    }

    const categoriasComValor = new Set(itens.map(it => it.categoriaId || it.CategoriaId)).size;
    if (categoriasComValor === 0) {
      Toast.warning(`Nenhum orçamento encontrado na Omie para ${resultado?.ano ?? ''}`);
    } else {
      Toast.success(`Orçamento da Omie importado: ${categoriasComValor} categoria(s). Revise e clique em "Salvar Orçamento".`);
    }
  }, [categories, touchedVM]);

  // Inicia a importação em segundo plano; o progresso fica no OmieBudgetImportContext
  const importFromOmie = useCallback(async (mode) => {
    if (!syncId || !year) return false;
    try {
      const status = await startImport({ syncId, syncName, ano: year, mode });
      if (status?.ano && status.ano !== year) {
        Toast.info(`Já existe uma importação do orçamento de ${status.ano} em andamento para esta empresa`);
      }
      return true;
    } catch (e) {
      console.error(e);
      const message = e?.response?.data?.notifications?.[0]?.message;
      Toast.error(message || 'Erro ao iniciar importação do orçamento da Omie');
      return false;
    }
  }, [syncId, syncName, year, startImport]);

  // Tela aberta: o progresso aparece inline (o painel flutuante some) e importações em andamento são retomadas
  useEffect(() => {
    if (!syncId) return undefined;
    const unregister = registerScreen(syncId);
    resumeImport({ syncId, syncName });
    return unregister;
  }, [syncId, syncName, registerScreen, resumeImport]);

  // Aplica o resultado quando a importação termina e o ano correspondente está carregado no grid
  const appliedJobsRef = useRef(new Set());
  useEffect(() => {
    if (!omieJob || omieJob.status !== 'completed' || omieJob.consumed || !omieJob.resultado) return;
    if (categories.length === 0 || loading || loadedYear !== omieJob.ano) return;
    if (appliedJobsRef.current.has(omieJob.jobId)) return;
    appliedJobsRef.current.add(omieJob.jobId);
    applyOmieResult(omieJob.resultado, omieJob.mode);
    dismissImport(syncId);
  }, [omieJob, categories, loading, loadedYear, applyOmieResult, dismissImport, syncId]);

  const dismissOmieImport = useCallback(() => {
    if (syncId) dismissImport(syncId);
  }, [syncId, dismissImport]);

  /**
   * Aplica regra de sinal por natureza antes de enviar à API:
   * - Sempre usa valor absoluto.
   * - Natureza R: envia o valor (positivo).
   * - Natureza P: envia o valor negativo.
   */
  function applyNatureToBudgetValue(value, natureza) {
    if (typeof value !== 'number' || Number.isNaN(value)) return value;
    const absVal = Math.abs(value);
    const nat = (natureza || 'R').toUpperCase();
    return nat === 'P' ? -absVal : absVal;
  }

  const buildItemsPayload = useCallback(() => {
    const items = [];
    // only leaves generate payload
    const uniqueLeaves = new Set();
    const collectLeaves = (nodes) => {
      nodes.forEach(n => {
        if (n.children && n.children.length > 0) {
          collectLeaves(n.children);
        } else {
          uniqueLeaves.add(n.id);
        }
      });
    };
    collectLeaves(categories);

    uniqueLeaves.forEach((catId) => {
      const node = idToNode.get(catId);
      const natureza = node?.natureza ?? node?.Natureza ?? 'R';

      for (let m = 1; m <= 12; m++) {
        const cur = budgets[catId]?.[m] || {};
        const item = {
          mes: m,
          categoriaId: catId
        };
        if (typeof cur.vm === 'number') item.valorMedio = applyNatureToBudgetValue(cur.vm, natureza);
        if (typeof cur.vp === 'number') item.valorPessimista = applyNatureToBudgetValue(cur.vp, natureza);
        if (typeof cur.vo === 'number') item.valorOtimista = applyNatureToBudgetValue(cur.vo, natureza);
        // sempre enviar tudo: incluir item mesmo que campos ausentes
        items.push(item);
      }
    });
    return items;
  }, [categories, budgets, idToNode]);

  const save = useCallback(async () => {
    if (!syncId || !year) return;
    try {
      setLoading(true);
      const payload = {
        syncId,
        ano: year,
        itens: buildItemsPayload()
      };
      await SynchronizationService.saveBudget(payload);
      Toast.success('Orçamento salvo com sucesso');
    } catch (e) {
      console.error(e);
      Toast.error('Erro ao salvar orçamento');
    } finally {
      setLoading(false);
    }
  }, [syncId, year, buildItemsPayload]);

  return {
    // state
    loading,
    importOmieLoading,
    omieJob,
    loadedYear,
    year,
    setYear,
    monthNames,
    categories,
    showVM, setShowVM,
    showVP, setShowVP,
    showVO, setShowVO,
    // values helpers
    isLeaf,
    setValue,
    getAggregatedValue,
    // actions
    fillFromAPI,
    importFromOmie,
    dismissOmieImport,
    save,
    recalculateVMByNature,
    recalculateVOByNature,
    recalculateVPByNature,
  };
}
